import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { NotificationContext, ConfirmContext } from '../components/Layout';
import { getTheme } from '../utils/theme';
import UserProfileModal from '../components/UserProfileModal';

const theme = getTheme();

function Avatar({ user, size = 44, onClick }) {
  const url = user?.sender_avatar || user?.avatar;
  const name = user?.sender_username || user?.username || '?';
  if (url) return <img src={url} alt={name} onClick={onClick} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid rgba(${theme.colors.primaryRGB},0.3)`, cursor: onClick?'pointer':'inherit' }} />;
  return <div onClick={onClick} style={{ width: size, height: size, borderRadius: '50%', background: theme.colors.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: size * 0.36, flexShrink: 0, cursor: onClick?'pointer':'inherit' }}>{name[0]?.toUpperCase()}</div>;
}

const SearchIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;

export default function FriendRequests() {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext) || {};
  const { confirm } = useContext(ConfirmContext) || {};
  const [tab, setTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  // Search state
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pendingRequests, setPendingRequests] = useState({}); // userId -> requestId or 'sent'
  const [viewProfileId, setViewProfileId] = useState(null);
  const [nextFriendsUrl, setNextFriendsUrl] = useState(null);
  const [loadingMoreFriends, setLoadingMoreFriends] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: reqData }, { data: frData }] = await Promise.all([
        api.get('/friend-requests/'),
        api.get('/friends/')
      ]);
      setRequests(reqData);
      setFriends(frData.results || frData);
      setNextFriendsUrl(frData.next || null);
    } catch (err) {
      console.error('Failed to load friends/requests', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMoreFriends = async () => {
    if (!nextFriendsUrl || loadingMoreFriends) return;
    setLoadingMoreFriends(true);
    try {
      const url = nextFriendsUrl.split('/api')[1] || nextFriendsUrl;
      const res = await api.get(url);
      setFriends(prev => [...prev, ...(res.data.results || [])]);
      setNextFriendsUrl(res.data.next || null);
    } catch {}
    setLoadingMoreFriends(false);
  };

  useEffect(() => { load(); }, [load]);

  // Debounced user search
  useEffect(() => {
    if (tab !== 'search') return;
    if (searchQ.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/users/search/?q=${encodeURIComponent(searchQ.trim())}`);
        setSearchResults(res.data);
      } catch {}
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ, tab]);

  const accept = async (id) => {
    try { await api.post(`/friend-requests/${id}/accept/`); showNotification?.('Friend request accepted!'); load(); } catch {}
  };

  const decline = async (id) => {
    try { 
      await api.post(`/friend-requests/${id}/reject/`); 
      setRequests(prev => prev.filter(r => r.id !== id));
      showNotification?.('Request declined.'); 
    } catch {}
  };

  const unfriend = async (friendId) => {
    const ok = await confirm({ title: 'Remove Friend?', message: 'Are you sure you want to remove this person from your friends?', icon: '👤', confirmText: 'Remove' });
    if (!ok) return;
    try { await api.post('/remove_friend/', { friend_id: friendId }); showNotification?.('Removed from friends.'); load(); } catch {}
  };

  const sendRequest = async (userId) => {
    try {
      const res = await api.post('/friend-requests/send/', { receiver_id: userId });
      setPendingRequests(prev => ({ ...prev, [userId]: res.data.id }));
      showNotification?.('Friend request sent!');
      setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, request_sent: true, request_id: res.data.id } : u));
    } catch (err) {
      showNotification?.(err.response?.data?.error || 'Failed to send request');
    }
  };

  const unsendRequest = async (userId, requestId) => {
    try {
      await api.post(`/friend-requests/${requestId}/unsend/`);
      setPendingRequests(prev => { const n = { ...prev }; delete n[userId]; return n; });
      setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, request_sent: false, request_id: null } : u));
      showNotification?.('Request withdrawn.');
    } catch {}
  };

  const tabs = [
    { id: 'requests', label: 'Requests', count: requests.length },
    { id: 'friends', label: 'Friends', count: friends.length },
    { id: 'search', label: 'Find People', count: 0 },
  ];

  return (
    <div style={S.root}>
      {viewProfileId && <UserProfileModal userId={viewProfileId} onClose={() => setViewProfileId(null)} />}
      <div style={S.header}>
        <h2 style={S.title}>Friends</h2>
      </div>

      <div style={S.tabs}>
        {tabs.map(t => (
          <button key={t.id} style={{ ...S.tab, ...(tab === t.id ? S.activeTab : {}) }} onClick={() => setTab(t.id)}>
            {t.label}
            {t.count > 0 && <span style={S.tabCount}>{t.count}</span>}
          </button>
        ))}
      </div>

      {loading && tab !== 'search' ? (
        <div style={S.empty}><div style={S.spinner} /></div>
      ) : tab === 'requests' ? (
        <div style={S.list}>
          {requests.length === 0 ? (
            <div style={S.empty}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="1.5" style={{ marginBottom: 12 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
              <div style={{ color: '#475569', fontSize: 13 }}>No pending requests</div>
            </div>
          ) : requests.map(req => (
            <div key={req.id} style={S.card}>
              <Avatar user={{ sender_avatar: req.sender_avatar, sender_username: req.sender_username }} onClick={() => setViewProfileId(req.sender_id)} />
              <div style={S.cardInfo}>
                <div style={S.cardName}>{req.sender_username}</div>
                <div style={S.cardSub}>Wants to be your friend</div>
              </div>
              <div style={S.actions}>
                <button style={S.acceptBtn} onClick={() => accept(req.id)}>Accept</button>
                <button style={S.declineBtn} onClick={() => decline(req.id)}>Decline</button>
              </div>
            </div>
          ))}
        </div>
      ) : tab === 'friends' ? (
        <div style={S.list}>
          {friends.length === 0 ? (
            <div style={S.empty}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="1.5" style={{ marginBottom: 12 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <div style={{ color: '#475569', fontSize: 13 }}>No friends yet</div>
              <div style={{ color: '#334155', fontSize: 11, marginTop: 4 }}>Try Random Chat to meet people</div>
              <button style={S.goBtn} onClick={() => navigate('/random')}>Go to Random Chat</button>
            </div>
          ) : friends.map(f => (
            <div key={f.id} style={S.card}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <Avatar user={f} onClick={() => setViewProfileId(f.id)} />
                <div style={{ width: 11, height: 11, borderRadius: '50%', background: f.is_online ? '#22c55e' : '#475569', position: 'absolute', bottom: 0, right: 0, border: '2px solid #111118' }} />
              </div>
              <div style={S.cardInfo}>
                <div style={S.cardName}>{f.username}</div>
                <div style={{ ...S.cardSub, color: f.is_online ? '#4ade80' : '#475569' }}>{f.is_online ? 'Online' : 'Offline'}</div>
              </div>
              <div style={S.actions}>
                <button style={S.msgBtn} onClick={() => navigate(`/chat/${f.id}`)}>Message</button>
                <button style={S.removeBtn} onClick={() => unfriend(f.id)}>Remove</button>
              </div>
            </div>
          ))}
          {nextFriendsUrl && (
            <button 
              style={{ ...S.goBtn, width: '100%', marginTop: 8 }} 
              onClick={loadMoreFriends} 
              disabled={loadingMoreFriends}
            >
              {loadingMoreFriends ? 'Loading...' : 'Load More Friends'}
            </button>
          )}
        </div>
      ) : (
        /* Search tab */
        <div style={S.searchWrap}>
          <div style={S.searchBox}>
            <span style={{ color: '#64748b', display: 'flex' }}><SearchIcon /></span>
            <input
              style={S.searchInput}
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search by username..."
              autoFocus
            />
            {searching && <div style={S.spin} />}
          </div>

          {searchQ.trim().length < 2 && (
            <div style={{ ...S.empty, paddingTop: 48 }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="1.5" style={{ marginBottom: 10 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <div style={{ color: '#475569', fontSize: 12 }}>Type at least 2 characters</div>
            </div>
          )}

          {searchQ.trim().length >= 2 && !searching && searchResults.length === 0 && (
            <div style={{ ...S.empty, paddingTop: 48 }}>
              <div style={{ color: '#475569', fontSize: 12 }}>No users found for "{searchQ}"</div>
            </div>
          )}

          <div style={S.list}>
            {searchResults.map(u => (
              <div key={u.id} style={S.card}>
                <Avatar user={u} onClick={() => setViewProfileId(u.id)} />
                <div style={S.cardInfo}>
                  <div style={S.cardName}>{u.username}</div>
                  <div style={S.cardSub}>
                    {u.is_friend ? 'Already friends' : u.request_received ? 'Sent you a request' : ''}
                  </div>
                </div>
                <div style={S.actions}>
                  {u.is_friend ? (
                    <button style={S.msgBtn} onClick={() => navigate(`/chat/${u.id}`)}>Message</button>
                  ) : u.request_received ? (
                    <button style={S.acceptBtn} onClick={() => { /* find the request */ load(); setTab('requests'); }}>See Request</button>
                  ) : u.request_sent ? (
                    <button style={S.unsendBtn} onClick={() => unsendRequest(u.id, u.request_id)}>Withdraw</button>
                  ) : (
                    <button style={S.addBtn} onClick={() => sendRequest(u.id)}>Add Friend</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { flex: 1, overflowY: 'auto', background: '#0a0a0f', display: 'flex', flexDirection: 'column' },
  header: { padding: '20px 20px 0' },
  title: { margin: 0, fontSize: 18, fontWeight: 800, color: '#e2e8f0' },
  tabs: { display: 'flex', padding: '14px 20px 0', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.06)', marginTop: 8 },
  tab: { padding: '8px 14px', border: 'none', background: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 600, borderBottom: '2px solid transparent', display: 'flex', alignItems: 'center', gap: 6, borderRadius: '8px 8px 0 0', transition: 'all 0.15s' },
  activeTab: { color: theme.colors.primary, borderBottomColor: theme.colors.primaryHover },
  tabCount: { background: '#ef4444', color: '#fff', borderRadius: 999, padding: '1px 6px', fontSize: 10, fontWeight: 700 },
  list: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
  card: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#111118', borderRadius: 14, border: '1px solid rgba(255,255,255,0.06)' },
  cardInfo: { flex: 1, overflow: 'hidden' },
  cardName: { fontWeight: 600, color: '#e2e8f0', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardSub: { fontSize: 10, color: '#475569', marginTop: 2 },
  actions: { display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' },
  acceptBtn: { padding: '7px 14px', borderRadius: 8, border: 'none', background: '#22c55e', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600 },
  declineBtn: { padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)', background: 'none', color: '#f87171', cursor: 'pointer', fontSize: 11 },
  msgBtn: { padding: '7px 14px', borderRadius: 8, border: 'none', background: theme.colors.primaryHover, color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600 },
  removeBtn: { padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 11 },
  addBtn: { padding: '7px 14px', borderRadius: 8, border: 'none', background: theme.colors.gradient, color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600 },
  unsendBtn: { padding: '7px 14px', borderRadius: 8, border: `1px solid rgba(${theme.colors.primaryRGB},0.4)`, background: 'none', color: theme.colors.primary, cursor: 'pointer', fontSize: 11 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#475569', flex: 1 },
  goBtn: { marginTop: 16, padding: '9px 20px', borderRadius: 10, border: 'none', background: theme.colors.gradient, color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  searchWrap: { display: 'flex', flexDirection: 'column', flex: 1 },
  searchBox: { display: 'flex', alignItems: 'center', gap: 10, margin: '14px 16px 0', padding: '10px 14px', background: '#111118', border: `1px solid rgba(${theme.colors.primaryRGB},0.2)`, borderRadius: 12 },
  searchInput: { flex: 1, background: 'none', border: 'none', outline: 'none', color: '#e2e8f0', fontSize: 13 },
  spin: { width: 16, height: 16, border: `2px solid rgba(${theme.colors.primaryRGB},0.3)`, borderTopColor: theme.colors.primaryHover, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 },
  spinner: { width: 28, height: 28, border: `3px solid rgba(${theme.colors.primaryRGB},0.2)`, borderTopColor: theme.colors.primaryHover, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
};
